import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface AuditRow {
  readonly auditLogId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly entityCode: string | null;
  readonly actorId: string | null;
  readonly action: string;
  readonly eventTimestamp: string;
}

const PAGE = 25;

/**
 * M40 — Audit Dashboard (§24). The read-only surface over the append-only audit
 * ledger (T534, LAW 3). Filter the immutable activity trail by event / entity /
 * actor / action and page through it. Viewing needs AUDIT_VIEW. There is no
 * mutate path — the ledger is never edited or deleted.
 */
export function AuditDashboard(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [eventType, setEventType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [entityCode, setEntityCode] = useState('');
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (eventType) params.set('eventType', eventType);
    if (entityType) params.set('entityType', entityType);
    if (entityCode) params.set('entityCode', entityCode);
    if (actorId) params.set('actorId', actorId);
    if (action) params.set('action', action);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: AuditRow[]; total: number }>(`/v1/app-platform/audit?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, eventType, entityType, entityCode, actorId, action, page]);

  return (
    <main aria-label="audit-dashboard">
      <h1>{t('m40.audit.title')}</h1>

      <section aria-label="audit-filters">
        <label>{t('m40.audit.eventType')}<input aria-label="filter-event-type" value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(0); }} /></label>
        <label>{t('m40.audit.entityType')}<input aria-label="filter-entity-type" value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(0); }} /></label>
        <label>{t('m40.audit.entityCode')}<input aria-label="filter-entity-code" value={entityCode} onChange={(e) => { setEntityCode(e.target.value); setPage(0); }} /></label>
        <RecordPicker<{ userId: string; displayName?: string; employeeCode?: string }>
          endpoint="/v1/users?limit=200"
          idField="userId"
          label={t('m40.audit.actorId')}
          testId="filter-actor-id"
          value={actorId}
          optionLabel={(u) =>
            [u.employeeCode, u.displayName].filter(Boolean).join(' \u2014 ') || u.userId
          }
          onPick={(id) => { setActorId(id); setPage(0); }}
          emptyText="No user on file"
        />
        <label>{t('m40.audit.action')}<input aria-label="filter-action" value={action} onChange={(e) => { setAction(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="audit-total">{t('m40.audit.total', { total })}</p>
          <DataTable<AuditRow>
            ariaLabel="audit-table"
            testId="audit-table"
            rowKey={(r) => r.auditLogId}
            rowDataAttrs={(r) => ({ 'data-audit': r.auditLogId })}
            emptyMessage={t('m40.audit.empty')}
            rows={rows}
            columns={[
              { key: 'timestamp', header: t('m40.audit.colTimestamp'), render: (r) => at(r.eventTimestamp) },
              { key: 'event', header: t('m40.audit.colEvent'), cellAriaLabel: (r) => `event-${r.auditLogId}`, render: (r) => r.eventType },
              { key: 'entity', header: t('m40.audit.colEntity'), render: (r) => r.entityType },
              { key: 'code', header: t('m40.common.code'), render: (r) => r.entityCode ?? '—' },
              { key: 'actor', header: t('m40.audit.colActor'), render: (r) => r.actorId ?? '—' },
              { key: 'action', header: t('m40.audit.action'), cellAriaLabel: (r) => `action-${r.auditLogId}`, render: (r) => r.action },
            ]}
          />

          <nav aria-label="audit-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m40.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m40.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m40.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
